// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).
// Form and metadata validation (requirements 3.1, 3.6; design 3.4).
import { CHOICE_TYPES } from '../types.js';
import { formToSchema, schemaToForm, toBdaSchema } from './convert.js';
export const FIELD_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;
export const BLUEPRINT_NAME_PATTERN = /^[A-Za-z0-9 _-]{1,128}$/;
export const MAX_FIELDS = 100;
export const MAX_DESCRIPTION = 500;
export const MAX_DOCUMENT_TYPE = 64;
/** BDA rejects a field instruction longer than 600 characters (task 13.7 probe: 600 accepted, 601 rejected). */
export const MAX_INSTRUCTION = 600;
/** BDA's limit on the schema JSON it accepts. */
export const MAX_SCHEMA_CHARS = 100_000;
/** Allowed values (task 13.7 probe): BDA accepts at most 20 per field, each at most 60 characters. */
export const MIN_OPTIONS = 2;
export const MAX_OPTIONS = 20;
export const MAX_OPTION_LENGTH = 60;
/** Characters BDA rejects in an allowed value (task 13.7 probe). */
export const OPTION_FORBIDDEN_CHARS = /[/{}~^`]/;
export function validateMetadata(meta) {
    const errors = {};
    if (!BLUEPRINT_NAME_PATTERN.test(meta.name) || meta.name.trim().length === 0) {
        errors.name = 'Name must be 1-128 characters: letters, digits, spaces, hyphens, or underscores.';
    }
    if (meta.documentType.trim().length === 0)
        errors.documentType = 'Document type is required.';
    else if (meta.documentType.length > MAX_DOCUMENT_TYPE)
        errors.documentType = `Document type must be at most ${MAX_DOCUMENT_TYPE} characters.`;
    if (meta.description.length > MAX_DESCRIPTION)
        errors.description = `Description must be at most ${MAX_DESCRIPTION} characters.`;
    return errors;
}
function checkName(name, path, seen, errors) {
    if (!FIELD_NAME_PATTERN.test(name)) {
        errors[`${path}.name`] =
            'Field names start with a letter and use only letters, digits, and underscores (max 64 characters).';
        return;
    }
    const key = name.toLowerCase();
    if (seen.has(key))
        errors[`${path}.name`] = `Duplicate field name "${name}".`;
    seen.add(key);
}
function checkInstruction(text, key, errors, required = true) {
    if (required && text.trim().length === 0)
        errors[key] = 'Instruction is required.';
    else if (text.length > MAX_INSTRUCTION)
        errors[key] = `Keep the instruction to ${MAX_INSTRUCTION} characters (${text.length} now).`;
}
/** Allowed values: 2-20 distinct (case-insensitive), non-blank values of at most 60 characters, on text fields only. */
export function optionsError(f) {
    const options = f.options;
    if (options === undefined)
        return undefined;
    if (!CHOICE_TYPES.includes(f.type))
        return 'Allowed values can only be set on text fields.';
    if (options.some((o) => o.trim().length === 0))
        return 'Remove the blank allowed value.';
    if (options.length < MIN_OPTIONS)
        return `Add at least ${MIN_OPTIONS} allowed values, one per line.`;
    if (options.length > MAX_OPTIONS)
        return `A field can have at most ${MAX_OPTIONS} allowed values (${options.length} now).`;
    const long = options.find((o) => o.length > MAX_OPTION_LENGTH);
    if (long !== undefined)
        return `Allowed values can be at most ${MAX_OPTION_LENGTH} characters: "${long.slice(0, 20)}…".`;
    const bad = options.find((o) => OPTION_FORBIDDEN_CHARS.test(o));
    if (bad !== undefined)
        return `Allowed values cannot contain / { } ~ ^ or \`: "${bad}".`;
    const seen = new Set();
    for (const o of options) {
        const key = o.trim().toLowerCase();
        if (seen.has(key))
            return `"${o.trim()}" is listed more than once.`;
        seen.add(key);
    }
    return undefined;
}
function checkScalar(f, p, errors) {
    checkInstruction(f.instruction, `${p}.instruction`, errors);
    const opt = optionsError(f);
    if (opt)
        errors[`${p}.options`] = opt;
}
function checkScalars(list, path, errors) {
    const seen = new Set();
    list.forEach((f, i) => {
        const p = `${path}.${i}`;
        checkName(f.name, p, seen, errors);
        checkScalar(f, p, errors);
    });
}
export function countFields(fields) {
    return fields.reduce((n, f) => n + (f.kind === 'field' ? 1 : f.kind === 'group' ? f.fields.length : f.columns.length), 0);
}
/** Length of the schema JSON as sent to BDA. */
export const schemaChars = (fields, meta = { documentType: '', description: '' }) => JSON.stringify(toBdaSchema(formToSchema({ name: '', ...meta, fields }))).length;
export function validateFields(fields, meta) {
    const errors = {};
    if (fields.length === 0)
        errors.fields = 'Add at least one field.';
    const seen = new Set();
    // Group and table names also become definition names, which must not collide.
    const defNames = new Set();
    fields.forEach((node, i) => {
        const p = `fields.${i}`;
        checkName(node.name, p, seen, errors);
        if (node.kind === 'field') {
            checkScalar(node, p, errors);
            return;
        }
        const def = node.kind === 'group' ? node.name.toLowerCase() : `${node.name.toLowerCase()}row`;
        if (defNames.has(def) && !errors[`${p}.name`])
            errors[`${p}.name`] = `"${node.name}" conflicts with another group or table.`;
        defNames.add(def);
        if (node.kind === 'group') {
            if (node.description.length > MAX_INSTRUCTION)
                errors[`${p}.description`] =
                    `Keep the description to ${MAX_INSTRUCTION} characters (${node.description.length} now).`;
            if (node.fields.length === 0)
                errors[`${p}.fields`] = 'A group needs at least one field.';
            checkScalars(node.fields, `${p}.fields`, errors);
        }
        else {
            checkInstruction(node.instruction, `${p}.instruction`, errors);
            if (node.columns.length === 0)
                errors[`${p}.columns`] = 'A table needs at least one column.';
            checkScalars(node.columns, `${p}.columns`, errors);
        }
    });
    if (countFields(fields) > MAX_FIELDS)
        errors.fields = `A blueprint can have at most ${MAX_FIELDS} fields.`;
    else {
        const chars = schemaChars(fields, meta);
        if (chars > MAX_SCHEMA_CHARS)
            errors.fields = `The blueprint schema is ${chars.toLocaleString('en-US')} characters; BDA accepts at most ${MAX_SCHEMA_CHARS.toLocaleString('en-US')}. Shorten instructions or remove fields.`;
    }
    return errors;
}
export function validateForm(form) {
    return { ...validateMetadata(form), ...validateFields(form.fields, form) };
}
export const isValid = (errors) => Object.keys(errors).length === 0;
/**
 * Save-time validation of a blueprint schema with its metadata (Req 3.6), shared by save and preview so both return
 * the same messages (create-doc-interactive Req 5.1, 5.2). Schemas the form cannot represent (JSON mode) pass here and
 * are left to BDA; representable ones get the full form validation. Returns null when valid.
 */
export function validateBlueprintSchema(meta, schema) {
    const res = schemaToForm(schema, meta.name);
    if (!res.form)
        return null;
    const errors = validateForm({
        ...res.form,
        name: meta.name,
        documentType: meta.documentType,
        description: meta.description,
    });
    return isValid(errors) ? null : errors;
}
//# sourceMappingURL=validate.js.map